import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { TileInfo, openTileUrl } from 'app/home/index.functions';
import React, { lazy, useEffect, useState } from 'react';
import IconMi from 'components/common/icon/IconMi';
import { usePrepareViewerData } from 'components/element-viewer/hooks/usePrepareViewerData';
import { FilterType } from 'components/external-reference/Filter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnyElementsViewerDataType, ElementViewerDataResponse, ElementViewerDataType } from 'components/element-viewer';
import { viewerAPI } from 'api/viewer';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useSearchParams } from 'react-router-dom';
import { DashboardElementType } from 'tools/types';
import { MetricViewerDataType } from 'components/metric/MetricViewer';
import { ElementPreviewSummary } from 'app/home/tile/preview/ElementPreviewSummary';
import { useReportPreviewSummaryStats } from 'app/home/tile/preview/hooks/useReportPreviewSummaryStats';
import useBundleTranslation from 'i18n';
import { useHomeContext } from 'app/home/context';
import { openInNewTab } from 'tools/tools';
import { dispatchCustomEvent } from 'tools/events';
import { useMiNavigation } from 'hooks/useNavigation';
import TopPanelWithBookmark from './top-panel/TopPanelWithBookmark';
const MetricPreview = lazy(() => import('./MetricPreview'));
export default function Preview({
    tile,
    onNextPreviewClick,
    hideNextPrevButtons,
    dataParams,
    refetchData,
}: {
    tile: TileInfo | null;
    onNextPreviewClick: (next: boolean) => void;
    hideNextPrevButtons?: boolean;
    dataParams?: { [key: string]: string };
    refetchData?: () => void;
}) {
    const { t } = useBundleTranslation(['app/home']);

    const [showPreviewPopup, setShowPreviewPopup] = useState(true);
    const [userChartOverlay, setUserChartOverlay] = useState<number>(0);
    const [filters, setFilters] = useState<Array<FilterType>>([]);
    const ctx = useHomeContext();
    const setTilePreview = ctx.setTilePreview;
    const customEvents = ctx.customEvents;
    const partsVisibility = ctx.visibility;
    const currentCatalogId = ctx.currentCatalogId;
    const elementId = Number(tile?.element_id);
    const segmentValueId = Number(tile?.segment_value_id);

    const [searchParams] = useSearchParams();
    const queryKey = `element_preview_${tile?.user_dashboard_element_instance_id}`;
    const queryClient = useQueryClient();
    useEffect(() => {
        return () => queryClient.removeQueries({ queryKey: [queryKey] });
    }, [queryKey]);
    const {
        data: viewerRequestData,
        refetch,
        isRefetching,
    } = useQuery<ElementViewerDataResponse, Error>({
        queryKey: [queryKey],
        queryFn: () => {
            return viewerAPI.loadElementData(
                elementId,
                Number(tile?.segment_value_id),
                true,
                userChartOverlay,
                false,
                true,
                undefined,
                searchParams,
                -1,
                -1,
                true,
            );
        },
    });

    const { viewerData, viewerExtraData } = usePrepareViewerData(
        userChartOverlay,
        setUserChartOverlay,
        setFilters,
        'preview',
        viewerRequestData,
    );

    const onPreviewHide = () => {
        setShowPreviewPopup(false);
        setTilePreview(null);
    };

    const navigate = useMiNavigation();

    const openElement = (
        type: DashboardElementType,
        elementId: number,
        segmentValueId?: number,
        e?: React.MouseEvent,
    ) => {
        if (null != tile) {
            if (undefined !== dataParams && undefined !== dataParams['on_click']) {
                if (
                    'url' == dataParams['on_click'] &&
                    undefined !== dataParams['redirect_url'] &&
                    dataParams['redirect_url'] > ''
                ) {
                    const newTab =
                        (undefined !== dataParams['new_tab'] && 'Y' == dataParams['new_tab']) ||
                        (undefined !== e && e.ctrlKey);

                    if (newTab) openInNewTab(dataParams['redirect_url']);
                    else document.location = dataParams['redirect_url'];
                    return;
                } else if ('event' == dataParams['on_click']) {
                    document.dispatchEvent(
                        new CustomEvent('mi-click', {
                            bubbles: false,
                            detail: {
                                props: dataParams,
                            },
                        }),
                    );
                    onPreviewHide();
                    return;
                }
            }
            return openTileUrl(tile, navigate, currentCatalogId, e);
        }
    };

    // TODO:
    const isEmptyInstance: any = 'N';
    const baseScr =
        process.env.REACT_APP_BACKEND_API_BASE_URL +
        `/data/content/index/preview/element/${elementId}/segment/${segmentValueId}?dashboard=Y`; // /dashboard/Y/external/Y

    const imageUrl =
        viewerData?.elementData?.displayFullImage && !('Y' == isEmptyInstance)
            ? baseScr
            : isEmptyInstance == 'Y'
              ? '/img/default_preview_no_data.png?dashboard=Y'
              : baseScr;
    const [imageTimestamp, setImageTimestamp] = useState(0);
    const [selectedBookmarkId, setSelectedBookmarkId] = useState(0);

    const onFavoritesChange = () => {
        refetchData && refetchData();
        refetch();
    };

    return tile && showPreviewPopup && viewerData?.elementData && viewerExtraData ? (
        <Dialog
            fullWidth
            maxWidth={'popupXl'}
            scroll={'body'}
            sx={{
                '.MuiDialog-paper': { overflow: 'visible', marginTop: '100px' },
                '.MuiDialog-container:after': {
                    content: 'none',
                },
            }}
            open={showPreviewPopup}
            onClose={onPreviewHide}
        >
            {isRefetching && <LoadingPlaceholder />}
            <DialogTitle id="alert-dialog-title" sx={{ py: '10px' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Stack direction={'row'} spacing={1}>
                        {true !== hideNextPrevButtons ? (
                            <>
                                <Button
                                    variant={'light'}
                                    startIcon={<IconMi icon="chevron-left" />}
                                    onClick={() => {
                                        onNextPreviewClick(false);
                                    }}
                                >
                                    {t('previous')}
                                </Button>
                                <Button
                                    variant={'light'}
                                    endIcon={<IconMi icon="chevron-right" />}
                                    onClick={() => {
                                        onNextPreviewClick(true);
                                    }}
                                >
                                    {t('next_preview')}
                                </Button>
                            </>
                        ) : (
                            <Stack>&nbsp;</Stack>
                        )}
                    </Stack>
                    <IconButton
                        aria-label="close"
                        onClick={onPreviewHide}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'primary.main',
                        }}
                    >
                        <IconMi icon="times" fontSize="16" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            {partsVisibility?.preview?.header !== false && (
                <DialogContent dividers sx={{ overflow: 'visible', py: '10px' }}>
                    <TopPanelWithBookmark
                        partsVisibility={partsVisibility?.preview}
                        customOnTitleClick={customEvents?.preview?.title_click}
                        openElement={openElement}
                        onFavoritesChange={onFavoritesChange}
                        viewerData={viewerData}
                        filters={filters}
                        tile={tile}
                        viewerExtraData={viewerExtraData}
                        onBookmarkChange={(timestamp: number, bookmarkId: number) => {
                            setImageTimestamp(timestamp);
                            setSelectedBookmarkId(bookmarkId);
                        }}
                    />
                </DialogContent>
            )}
            <DialogContent sx={{ overflow: 'visible' }}>
                <PreviewContent
                    customOnImageClick={customEvents?.preview?.image_click}
                    imageUrl={`${imageUrl}&t=${imageTimestamp}`}
                    bookmark={selectedBookmarkId}
                    refetch={refetch}
                    openElement={openElement}
                    viewerData={viewerData}
                    viewerExtraData={viewerExtraData}
                    segmentValueId={segmentValueId}
                />
            </DialogContent>
        </Dialog>
    ) : (
        <LoadingPlaceholder />
    );
}

export function PreviewContent({
    viewerData,
    viewerExtraData,
    segmentValueId,
    openElement,
    refetch,
    imageUrl,
    customOnImageClick,
    bookmark,
}: {
    viewerData: ElementViewerDataType;
    viewerExtraData: AnyElementsViewerDataType;
    segmentValueId: number;
    openElement: (type: DashboardElementType, elementId: number, segmentValueId?: number) => void;
    refetch: () => void;
    imageUrl: string;
    customOnImageClick?: boolean;
    bookmark: number;
}) {
    const { t } = useBundleTranslation(['components/common/element']);
    const [imageLoaded, setImageLoaded] = useState(false);
    const elementId = viewerData.elementData.row.elementId;
    const reportPreviewSummaryStats = useReportPreviewSummaryStats(viewerExtraData, elementId, segmentValueId);

    const viewerProps = {
        crumbsData: viewerData.crumbsData,
        related: [],
        filters: [],
        onFiltersChange: () => {},
        onSegmentValueChange: () => {},
        segmentValueId: segmentValueId,
        onFavoritesChange: () => {},
        onRelatedElementSelection: () => {},
        onFilterChange: () => {},
        refetch: refetch,
    };

    const handleClick = () => {
        if (customOnImageClick) {
            dispatchCustomEvent('mi-preview', {
                action: 'image-click',
                payload: { element: viewerData.elementData.row },
            });
            return;
        }
        openElement(viewerData.elementData.row.type, elementId, segmentValueId);
    };

    const onLoad = () => {
        setImageLoaded(true);
    };

    const summaryComponent = (
        <ElementPreviewSummary
            elementInfo={viewerData?.elementData}
            viewerRequestExtraData={
                reportPreviewSummaryStats ? ({ metrics: [reportPreviewSummaryStats] } as MetricViewerDataType) : viewerExtraData
            }
            onToggleVisibility={() => {}}
            isVisible
            isPreview
            isReportMetric={!!reportPreviewSummaryStats}
        />
    );

    if (['metric', 'multi-metric chart'].includes(viewerData.elementData.row.type)) {
        return (
            <Stack direction="row" sx={{ overflow: 'hidden' }}>
                <MetricPreview
                    openElement={openElement}
                    elementInfo={viewerData?.elementData}
                    viewerRequestExtraData={viewerExtraData as MetricViewerDataType}
                    viewerRequestData={viewerData}
                    {...viewerProps}
                />
                {summaryComponent}
            </Stack>
        );
    }

    if (
        viewerData.elementData.row.type == 'external report' &&
        undefined !== viewerData.elementData?.noToken &&
        true === viewerData.elementData?.noToken
    ) {
        openElement(viewerData.elementData.row.type, elementId, segmentValueId);
        return null;
    }

    if (
        viewerData.elementData.row.type == 'external report' ||
        viewerData.elementData.row.type == 'other external content'
    ) {
        return (
            <Stack direction="row" sx={{ overflow: 'hidden' }}>
                <Box flexGrow={1} sx={{ overflow: 'hidden' }}>
                    <Box sx={{ display: 'inline-flex', maxWidth: '100%', cursor: 'pointer' }} onClick={handleClick}>
                        <img style={{ maxWidth: '100%' }} id="preview_image_el" src={imageUrl} onLoad={onLoad} />
                    </Box>
                    {!imageLoaded && viewerData.elementData.row.type == 'external report' && (
                        <Stack justifyContent={'center'} alignItems={'center'} spacing={2} sx={{ minHeight: '200px' }}>
                            <CircularProgress size={24} />
                            <Typography>{t('preview_preparing')}</Typography>
                            <Button variant={'text'} sx={{ width: '30%' }} onClick={handleClick}>
                                {t('skip_preview')}
                            </Button>
                        </Stack>
                    )}
                </Box>

                {summaryComponent}
            </Stack>
        );
    }

    if (viewerData.elementData.row.type == 'internal report') {
        return (
            <Stack direction="row" sx={{ overflow: 'hidden' }}>
                <Stack direction="column">
                    <Box flexGrow={1}>
                        <span>
                            <iframe
                                style={{ border: 'none', overflow: 'hidden', width: 750, height: 516 }}
                                src={`/report/${elementId}/segment/${segmentValueId}/preview?width=750&height=500&view_type=tile&bookmark=${bookmark}`}
                            />
                        </span>
                    </Box>
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <Button variant="light" startIcon={<IconMi icon="view" />} onClick={handleClick}>
                            {t('view_full_report')}
                        </Button>
                    </Box>
                </Stack>
                {summaryComponent}
            </Stack>
        );
    }

    return null;
}
